/**
 * Dashboard KPI hook with mock fallback.
 *
 * Only the four headline KPIs are sourced from `/reports/dashboard`. The charts
 * (monthly series) and the top-clients / wallets / recent-activity lists are not
 * exposed by the API in Phase 5.1, so the page keeps those on mock data. When
 * not authenticated or the API errors, the KPIs also fall back to mock.
 */

import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import { useAuthStore } from "@/features/auth/auth.store";
import { WALLET_ALERT_THRESHOLD_USD } from "@/lib/constants";
import { clients as mockClients, wallets as mockWallets, payments as mockPayments } from "@/mocks/seed";
import { dashboardApi, type DashboardKpis, type DashboardPeriod } from "./dashboard.api";

type DataSource = "api" | "mock";

/** Recompute the API KPI shape from mock data so the fallback is consistent. */
function mockKpis(): DashboardKpis {
  const serviceRevenueDzd = mockPayments
    .filter((p) => p.status === "RECEIVED")
    .reduce((s, p) => s + p.amount, 0);
  return {
    serviceRevenueDzd,
    adSpendUsd: mockClients.reduce((s, c) => s + c.adSpendUsd, 0),
    walletBalanceUsd: mockWallets.reduce((s, w) => s + w.balanceUsd, 0),
    netProfitDzd: Math.round(serviceRevenueDzd * 0.44),
    clientsActive: mockClients.filter((c) => c.status === "ACTIVE").length,
    lowWallets: mockWallets.filter((w) => w.balanceUsd < WALLET_ALERT_THRESHOLD_USD).length,
    pendingPayments: mockPayments.filter((p) => p.status === "PENDING").length,
    overduePayments: mockPayments.filter((p) => p.status === "OVERDUE").length,
    totalExpensesDzd: 0,
  };
}

export interface UseDashboardResult {
  kpis: DashboardKpis;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useDashboardReport(period: DashboardPeriod): UseDashboardResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";
  const fallback = mockKpis();

  const query = useQuery({
    queryKey: queryKeys.dashboard(period),
    queryFn: () => dashboardApi.get(period),
    enabled: authed,
  });

  if (!authed) return { kpis: fallback, isLoading: false, isError: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { kpis: query.data.kpis, isLoading: false, isError: false, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { kpis: fallback, isLoading: false, isError: true, source: "mock", error: query.error, refetch: query.refetch };
  // While loading, show mock numbers rather than blanks; they swap to API on success.
  return { kpis: fallback, isLoading: query.isLoading, isError: false, source: "api", error: null, refetch: query.refetch };
}
